import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import ShareIcon from '@mui/icons-material/Share';
import InboxIcon from '@mui/icons-material/Inbox';
import ChatIcon from '@mui/icons-material/Chat';
import RateReviewIcon from '@mui/icons-material/RateReview';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PublishIcon from '@mui/icons-material/Publish';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FavoriteIcon from '@mui/icons-material/Favorite';
import ScheduleIcon from '@mui/icons-material/Schedule';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import { socialApi } from '@/api';
import { DashboardLayout } from '@/shared/layouts/DashboardLayout';
import { KpiGrid } from '@/components/ui/KpiGrid';
import { StatCard } from '@/components/ui/StatCard';
import { DashboardSkeleton } from '@/components/ui/Skeleton';
import { PageSection } from '@/components/ui/PageSection';
import type {
  SocialAnalyticsDashboard,
  SocialDashboardSummary,
  SocialMonitoringSummary,
} from '@/types/social';
import { showApiError } from '@/utils/apiError';
import { SocialMiniCharts } from '../components/SocialMiniCharts';
import { ModuleWorkQueuePanel } from '@/components/workqueue/ModuleWorkQueuePanel';
import { SOCIAL_PLATFORMS } from '../config/platformCapabilities';
import { useSocialAccounts } from '../hooks/useSocialAccounts';

export function SocialCommandCenterPage() {
  const [stats, setStats] = useState<SocialDashboardSummary | null>(null);
  const [monitoring, setMonitoring] = useState<SocialMonitoringSummary | null>(null);
  const [analytics, setAnalytics] = useState<SocialAnalyticsDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const { connected } = useSocialAccounts();

  useEffect(() => {
    Promise.all([
      socialApi.dashboardSummary().then(setStats),
      socialApi.monitoringSummary().then(setMonitoring).catch(() => undefined),
      socialApi.analyticsDashboard().then(setAnalytics).catch(() => undefined),
    ])
      .catch((err) => showApiError(err, 'Failed to load command center'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <DashboardLayout
        moduleLabel="Social"
        title="Social Command Center"
        description="Unified management across every connected network"
      >
        <DashboardSkeleton kpiCount={8} />
      </DashboardLayout>
    );
  }

  const messages = monitoring?.unreadMessages ?? analytics?.messages ?? 0;
  const comments = monitoring?.pendingReplies ?? 0;
  const reviews = monitoring?.openReviews ?? 0;

  return (
    <DashboardLayout
      moduleLabel="Social"
      title="Social Command Center"
      description="Hootsuite-class unified hub with dedicated platform workspaces"
      actions={
        <Link to="/social/publishing" className="text-sm font-medium text-primary-600 hover:underline dark:text-primary-400">
          Publishing Center →
        </Link>
      }
    >
      <KpiGrid columns={4}>
        <StatCard title="Messages" value={messages} icon={<InboxIcon />} />
        <StatCard title="Comments" value={comments} icon={<ChatIcon />} />
        <StatCard title="Reviews" value={reviews} icon={<RateReviewIcon />} />
        <StatCard title="Leads generated" value={stats?.socialLeads ?? 0} icon={<PersonAddIcon />} />
        <StatCard title="Published posts" value={stats?.publishedPosts ?? 0} icon={<PublishIcon />} />
        <StatCard
          title="Channel health"
          value={stats?.averageChannelHealth != null ? `${stats.averageChannelHealth}%` : '—'}
          icon={<HealthAndSafetyIcon />}
        />
        <StatCard title="Open comments" value={stats?.openComments ?? comments} icon={<ChatIcon />} />
        <StatCard title="Reach" value={analytics?.reach?.toLocaleString() ?? '—'} icon={<VisibilityIcon />} />
        <StatCard title="Engagement" value={analytics?.engagement?.toLocaleString() ?? '—'} icon={<FavoriteIcon />} />
        <StatCard
          title="Response time"
          value={analytics?.avgResponseMinutes != null ? `${analytics.avgResponseMinutes}m` : '—'}
          icon={<ScheduleIcon />}
        />
      </KpiGrid>

      <SocialMiniCharts trend={analytics?.engagementTrend} channels={analytics?.channelPerformance} />

      {connected.length === 0 && (
        <div className="card border-primary-200 bg-primary-50/50 p-5 dark:border-primary-800 dark:bg-primary-950/30">
          <p className="font-semibold text-slate-900 dark:text-slate-100">Get started</p>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
            Connect Facebook, Instagram, WhatsApp, LinkedIn, and more — you can add multiple accounts per network.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Link to="/social/connect?tab=guides">
              <Button size="sm">Read setup guides</Button>
            </Link>
            <Link to="/social/connect?tab=channels">
              <Button size="sm" variant="secondary">
                Add your first account
              </Button>
            </Link>
          </div>
        </div>
      )}

      <ModuleWorkQueuePanel module="SOCIAL" title="Today's work" />

      <PageSection title="Connected platforms" description="Open a dedicated workspace — only supported features appear">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {SOCIAL_PLATFORMS.map((p) => {
            const isConnected = connected.some(
              (a) => a.platform.toUpperCase() === p.apiPlatform || a.platform.toUpperCase().startsWith(p.apiPlatform.split('_')[0] ?? ''),
            );
            return (
              <Link
                key={p.id}
                to={`/social/platforms/${p.id}`}
                className="card flex items-center gap-3 p-4 transition hover:ring-2 hover:ring-primary-500/40"
              >
                <span
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-sm font-bold text-white"
                  style={{ backgroundColor: p.brandColor }}
                >
                  {p.label.slice(0, 2)}
                </span>
                <div className="min-w-0">
                  <p className="font-medium text-slate-900 dark:text-slate-100">{p.label}</p>
                  <p className="text-xs text-slate-500">
                    {isConnected ? 'Connected' : 'Not connected'} · {p.tabs.length} areas
                  </p>
                </div>
              </Link>
            );
          })}
        </div>
      </PageSection>

      <PageSection title="Quick actions">
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
          {[
            { to: '/social/inbox', label: 'Unified Inbox', icon: <InboxIcon /> },
            { to: '/social/publishing', label: 'Publishing Center', icon: <PublishIcon /> },
            { to: '/social/calendar', label: 'Content Calendar', icon: <ScheduleIcon /> },
            { to: '/social/analytics', label: 'Analytics', icon: <ShareIcon /> },
            { to: '/social/automation', label: 'Automation', icon: <ScheduleIcon /> },
            { to: '/social/crm-leads', label: 'CRM Leads', icon: <PersonAddIcon /> },
            { to: '/social/comments', label: 'Comments Center', icon: <ChatIcon /> },
            { to: '/social/reviews', label: 'Reviews Center', icon: <RateReviewIcon /> },
            { to: '/social/media', label: 'Media Library', icon: <PhotoLibraryIcon /> },
            { to: '/social/connect', label: 'Connection Center', icon: <ShareIcon /> },
            { to: '/social/settings', label: 'Settings', icon: <ShareIcon /> },
          ].map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className="card flex items-center gap-2 p-3 text-sm font-medium hover:bg-slate-50 dark:hover:bg-slate-800"
            >
              {item.icon}
              {item.label}
            </Link>
          ))}
        </div>
      </PageSection>
    </DashboardLayout>
  );
}
